<template>
  <div class="flex flex-column flex-grow-1">
    <NuxtLoadingIndicator />
    <NuxtLayout :menu-items="menuItems">
      <template #app-sidebar>
        <component
          v-if="sidebarComponent"
          :is="sidebarComponent"
          :key="sidebar"
        />
      </template>
      <NuxtPage />
    </NuxtLayout>

    <!-- Right Drawer-->
    <div
      v-if="permissionsStore.canViewScheduledActions()"
      class="right-drawer"
      :class="{ 'open': isRightDrawerOpen }">
      <div class="right-drawer-header">
        <h3>{{ t('menu.scheduledActions') }}</h3>
        <button @click="isRightDrawerOpen = false" class="close-btn">
          <i class="pi pi-times"></i>
        </button>
      </div>
      <div class="right-drawer-content">
        <!-- Your scheduled actions content here -->
        <ScheduledActionsDataView />
      </div>
    </div>
    <div
      v-if="isRightDrawerOpen && permissionsStore.canViewScheduledActions()"
      class="right-drawer-backdrop"
      @click="isRightDrawerOpen = false">
    </div>

    <client-only>
      <Toast position="bottom-right" />
      <ModuleMessageQueue />
    </client-only>
  </div>
</template>

<script setup lang="ts">
import './assets/css/global.css';
import capitalize from '~/Common/StringOperations';
import ScheduledActionsDataView from '~/components/module/ScheduledActionsDataView.vue';
import { useI18n } from 'vue-i18n';
import { usePermissionsStore } from '~/stores/permissions';
import { onMounted, watch } from 'vue';
import { useUsersStore } from '~/stores/user';

const { t } = useI18n();

const route = useRoute();
const permissionsStore = usePermissionsStore();
const usersStore = useUsersStore();

const sidebar = computed(() =>{
  const routeName = route.name?.toString() || "";
  if (routeName.includes("userGroups")) {
    return "userGroups";
  } else if (routeName.includes("roles")) {
    return "roles";
  } else if (routeName.includes("settings")) {
    return "settings";
  } else if (routeName.includes("user")) {
    return "users";
  } else if (routeName.includes("Profile")) {
    return "configProfile";
  } else if(routeName.includes("tenants")){
    return "tenants";
  } else if (routeName.includes("config")) {
    return "config";
  }  else if (routeName.includes("schedule")) {
    return "schedule";
  }
  if (
    routeName.includes('clients') ||
    routeName.includes('clientGroups')
  ) {
    return 'clients';
  }

  return null;
});

const sidebarComponent = computed(() => {
  if (!sidebar.value) {
    return null;
  }

  try {
    const componentName = `ModuleSidebar${capitalize(
      sidebar.value.replace('/', '')
    )}`;
    return resolveComponent(componentName);
  } catch (e) {
    console.error(`Failed to resolve sidebar component for ${sidebar.value}`, e);
    return resolveComponent("ModuleSidebarClients");
  }
});

const isRightDrawerOpen = ref(false);

// Watch for user availability and fetch permissions
watch(
  () => usersStore.users[0]?.id,
  async (userId) => {
    if (userId && !permissionsStore.loading && !permissionsStore.permissions.length) {
      await permissionsStore.fetchPermissions(userId);
    }
  },
  { immediate: true }
);

// Update menuItems computed to check user loading
const menuItems = computed(() => {
  const items = [
    {
      label: t('menu.clients'),
      icon: "pi-desktop",
      to: "/clients",
      permission: () => permissionsStore.canViewClients(),
    },
    {
      label: t('menu.configProfiles'),
      icon: "pi-id-card",
      to: "/configProfile",
      permission: () => permissionsStore.canRead('configProfile'),
    },
    {
      label: t('menu.users'),
      icon: "pi-user-edit",
      to: "/users",
      permission: () => permissionsStore.canRead('user'),
    },
    {
      label: t('menu.roles'),
      icon: "pi-prime",
      to: "/roles",
      permission: () => permissionsStore.canRead('role'),
    },
    {
      label: t('menu.userGroups'),
      icon: "pi-users",
      to: "/userGroups",
      permission: () => permissionsStore.canRead('userGroup'),
    },
    {
      label: t('menu.scheduledActions'),
      icon: "pi-clock",
      to: "#",
      active: false,
      action: () => {
        isRightDrawerOpen.value = !isRightDrawerOpen.value;
        const scheduledItem = menuItems.value.find(item => item.label === t('menu.scheduledActions'));
        if (scheduledItem) {
          scheduledItem.active = isRightDrawerOpen.value;
        }
      },
      permission: () => permissionsStore.canViewScheduledActions(),
    },
    {
      label: t('menu.auditLog'),
      icon: "pi-list",
      to: "/audit-log",
      permission: () => permissionsStore.canRead('auditLog'),
    },
    {
      label: t('menu.tenants'),
      icon: "pi-user",
      to: "/tenants",
      permission: () => permissionsStore.canShowTenantsMenu(),
    },
    {
      label: t('menu.settings'),
      icon: "pi-cog",
      to: "/settings",
      permission: () => permissionsStore.canShowSettings(),
    }
  ];
  return items.filter(item => item.permission && item.permission());
});

// Fetch permissions on mount if not already loaded
onMounted(async () => {
  // Fetch permissions for logged-in user first
  let loggedInUserId = usersStore.users[0]?.id;
  if (!loggedInUserId && typeof window !== 'undefined') {
    const storedId = localStorage.getItem('userId');
    if (storedId) {
      loggedInUserId = Number(storedId);
    }
  }
  if (!permissionsStore.loading && !permissionsStore.permissions.length && loggedInUserId) {
    await permissionsStore.fetchPermissions(loggedInUserId);
  }

  // Hydrate users only when permission allows listing users.
  if (!usersStore.users.length && !usersStore.loading) {
    if (permissionsStore.canRead('user')) {
      await usersStore.getAllUsers();
    }
  }
});
</script>

<style>
:root {
  --sidebar-width: 4rem;
  --sidebar-content-width: 300px;
  --total-sidebar-width: calc(var(--sidebar-width) + var(--sidebar-content-width));
}

.p-toast-message-text {
  display: flex !important;
  align-items: center !important;
}

.p-toast-summary {
  vertical-align: bottom;
}

.p-toast-message-content {
  display: flex;
  justify-content: center;
  align-items: center;
}

.p-toast-message-icon {
  height: 2rem;
  width: 1.5rem;
}

a {
  text-decoration: none;
}

/* Right drawer styles */
.right-drawer {
  position: fixed;
  top: 0;
  right: -50%;
  width: 50%;
  height: 100vh;
  background-color: hsl(var(--background));
  color: hsl(var(--foreground));
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: right 0.3s ease;
  display: flex;
  flex-direction: column;
}

.right-drawer.open {
  right: 0;
}


.right-drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid hsl(var(--border));
}

.right-drawer-content {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
}

.close-btn {
  background-color: hsl(var(--input));
  color: hsl(var(--foreground) / 0.5);
  border-radius: 4px  ;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
}
.right-drawer-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}
</style>
